/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import RetailerUserModel from "../components/RetailerUsers/RetailerUserModel";

/**
 * A custom React hook that synchronizes the current retailer user information with the server using WebSocket.
 * It listens for updates on the retailer user's information and triggers a Redux action
 * to fetch the latest data when the information is updated or when the tab becomes visible.
 *
 * @returns {function} Returns the fetchRetailerUserInfo function to be executed when needed.
 *
 * @example
 * // Usage in a component
 * const SomeComponent = () => {
 *   const fetchRetailerUserInfo = useSyncRetailerUserInfo();
 *
 *   useEffect(() => {
 *     // Fetch retailer user info once when the component mounts
 *     fetchRetailerUserInfo();
 *   }, [fetchRetailerUserInfo]);
 *
 *   return <div>Your component content</div>;
 * }
 */
const useSyncRetailerUserInfo = () => {
  const dispatch = useDispatch();

  /**
   * Fetch and dispatch retailer users information.
   * This function retrieves the latest retailer users info from the server
   * and dispatches an action to update the Redux store.
   *
   * @returns {void} This function does not return any value.
   */
  const fetchRetailerUserInfo = () => {
    RetailerUserModel.getCurrentRetailerUserInfo().then((data) => {
      dispatch({ type: 'GET_CURRENT_RETAILER_USER_INFO', data });
    }).catch((error) => {
      // Solo loggear errores que no sean de autenticación (401, 403)
      // para evitar ruido en la consola cuando el usuario no está logueado
      if (error?.status && ![401, 403].includes(error.status)) {
        console.error('Error fetching retailer user info:', error);
      }
    });
  };

  /**
   * Fetch and dispatch retailer users information.
   * This function retrieves the latest retailer users info from the server
   * and dispatches an action to update the Redux store.
   *
   * @returns {void} This function does not return any value.
   */
  const fetchRetailerUser = () => {
    RetailerUserModel.getCurrentRetailerUser().then((data) => {
      dispatch({ type: 'SET_CURRENT_RETAILER_USER', data });
    }).catch((error) => {
      // Solo loggear errores que no sean de autenticación (401, 403)
      // para evitar ruido en la consola cuando el usuario no está logueado
      if (error?.status && ![401, 403].includes(error.status)) {
        console.error('Error fetching retailer user:', error);
      }
    });
  };

  useEffect(() => {
    let isComponentMounted = true;

    /**
     * Callback function that handles the received status updates from the WebSocket.
     * Dispatches the action to set the current retailer user information when the status is true.
     *
     * @param {Object} data - The data received from the WebSocket.
     * @param {boolean} data.status - The status of the update.
     */
    const statusListener = (data) => {
      if (data.status === true && isComponentMounted) {
        fetchRetailerUser();
        fetchRetailerUserInfo();
      }
    };

    // Use the existing socket
    if (socket) {
      socket.on('update_retailer_user_info', statusListener);
    }

    return () => {
      isComponentMounted = false;
      if (socket) {
        socket.off('update_retailer_user_info', statusListener);
      }
    };
  }, []);

  // Return the fetchRetailerUserInfo function for external use
  return { fetchRetailerUserInfo, fetchRetailerUser };
};

export default useSyncRetailerUserInfo;
